import PropTypes from "prop-types";
import React from "react";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import { speak } from "@wordpress/a11y";
import ProfileForm from "./account/profile/ProfileForm";
import ConnectedApplications from "./account/profile/ConnectedApplications";
import SubscribeNewsletter from "./account/profile/SubscribeNewsletter";
import DeleteAccount from "./account/profile/dangerzone/DeleteAccount";
import DownloadAccount from "./account/profile/dangerzone/DownloadAccount";
import PasskeyManagement from "./account/profile/passkeys/PasskeyManagement";
import PasswordResetForm from "./account/profile/PasswordResetForm";
import { ErrorPropTypeShape } from "../errors/ErrorDisplay";
import * as styles from "./ProfilePageStyles.module.scss";
import { Title } from "@yoast/ui-library";
import { PageHeader } from "./PageHeader";

const messages = defineMessages( {
	title: {
		id: "validation.page.title",
		defaultMessage: "Profile",
	},
	validationFormatEmail: {
		id: "validation.format.email",
		defaultMessage: "{field} must be a valid e-mail address.",
	},
	validationRequired: {
		id: "validation.required",
		defaultMessage: "{field} cannot be empty.",
	},
	duplicateEmail: {
		id: "profile.error.duplicateEmail",
		defaultMessage: "The email address could not be changed, it is probably already in use.",
	},
	labelEmail: {
		id: "profile.label.email",
		defaultMessage: "Email",
	},
	labelDelete: {
		id: "profile.label.delete",
		defaultMessage: "Delete account",
	},
	dangerZone: {
		id: "profile.label.dangerZone",
		defaultMessage: "Download your data or delete your account",
	},
	saving: {
		id: "profile.saving",
		defaultMessage: "Saving...",
	},
	saved: {
		id: "profile.saved",
		defaultMessage: "Email address saved",
	},
	saveEmail: {
		id: "profile.saveEmail",
		defaultMessage: "Save email address",
	},
	deleteAccount: {
		id: "profile.delete",
		defaultMessage: "Delete your account",
	},
	passwordChange: {
		id: "profile.passwordChange",
		defaultMessage: "Password",
	},
	personalInfo: {
		id: "personal.info",
		defaultMessage: "Personal info",
	},
	profilePageLoaded: {
		id: "menu.account.orders.loaded",
		defaultMessage: "Account profile page loaded",
	},
	newsLetter: {
		id: "newsLetter",
		defaultMessage: "Yoast SEO Newsletter",
	},
	connectedApplications: {
		id: "profile.connectedApplications",
		defaultMessage: "Connected applications",
	},
	connectedApplicationsDescription: {
		id: "profile.connectedApplicationsDescription",
		defaultMessage: "Applications you've granted access to your MyYoast account.",
	},
} );

/**
 * Returns the rendered Sites Page component.
 *
 * @param {Object} props The props to use.
 *
 * @returns {ReactElement} The rendered Sites component.
 */
class ProfilePage extends React.Component {
	/**
	 * Sets the ProfilePage object.
	 *
	 * Sets (input) validation constraints, including email.
	 *
	 * @param {Object} props The props passed to the component.
	 * @returns {void}
	 */
	/**
	 * Actions to perform when the component mounted.
	 *
	 * @returns {void}
	 */
	componentDidMount() {
		// Announce navigation to assistive technologies.
		const message = this.props.intl.formatMessage( messages.profilePageLoaded );
		speak( message );
	}

	/**
	 * Whether we are currently disabling the account.
	 *
	 * @returns {boolean} Whether we are currently disabling the account.
	 */
	isDeleting() {
		return this.props.isDeleting;
	}

	/**
	 * Renders the component.
	 *
	 * @returns {ReactElement} The rendered component.
	 */
	render() {
		return (
			<div>
				<PageHeader title={ messages.title } />
				<section className={ styles.pageSection }>
					<div className={ styles.sectionTitle }>
						<Title as="h2" size="4">
							<FormattedMessage
								id={ messages.personalInfo.id }
								defaultMessage={ messages.personalInfo.defaultMessage }
							/>
						</Title>
					</div>
					<div className={ styles.sectionContent }>
						<ProfileForm { ...this.props } />
					</div>
				</section>
				<section className={ styles.pageSection }>
					<div className={ styles.sectionTitle }>
						<Title as="h2" size="4">
							<FormattedMessage
								id={ messages.passwordChange.id }
								defaultMessage={ messages.passwordChange.defaultMessage }
							/>
						</Title>
						<p>
							<FormattedMessage
								id="profile.description.passwordReset"
								defaultMessage="Your password should be at least 8 characters long,
										contain both uppercase and lowercase letters and one symbol."
							/>
						</p>
					</div>
					<div className={ styles.sectionContent }>
						<PasswordResetForm { ...this.props } />
					</div>
				</section>
				<section className={ styles.pageSection }>
					<div className={ styles.sectionTitle }>
						<Title as="h2" size="4">
							<FormattedMessage
								id="profile.passkeys"
								defaultMessage="Passkeys"
							/>
						</Title>
						<p>
							<FormattedMessage
								id="profile.passkeys.description"
								defaultMessage="Passkeys let you sign in quickly and securely using your fingerprint, face, or screen lock."
							/>
						</p>
					</div>
					<div className={ styles.sectionContent }>
						<PasskeyManagement />
					</div>
				</section>
				<section className={ styles.pageSection }>
					<div className={ styles.sectionTitle }>
						<Title as="h2" size="4">
							<FormattedMessage
								id={ messages.newsLetter.id }
								defaultMessage={ messages.newsLetter.defaultMessage }
							/>
						</Title>
					</div>
					<div className={ styles.sectionContent }>
						<SubscribeNewsletter email={ this.props.email } />
					</div>
				</section>
				<section className={ styles.pageSection }>
					<div className={ styles.sectionTitle }>
						<Title as="h2" size="4">
							<FormattedMessage
								id={ messages.connectedApplications.id }
								defaultMessage={ messages.connectedApplications.defaultMessage }
							/>
						</Title>
						<p>
							<FormattedMessage
								id={ messages.connectedApplicationsDescription.id }
								defaultMessage={ messages.connectedApplicationsDescription.defaultMessage }
							/>
						</p>
					</div>
					<div className={ styles.sectionContent }>
						<ConnectedApplications />
					</div>
				</section>
				<section>
					<DownloadAccount />
				</section>
				<section>
					<DeleteAccount
						onDeleteProfile={ this.props.onDeleteProfile }
						isDeleting={ this.props.isDeleting }
					/>
				</section>
			</div>

		);
	}
}

ProfilePage.propTypes = {
	// User data
	intl: intlShape.isRequired,
	email: PropTypes.string.isRequired,
	userFirstName: PropTypes.string,
	userLastName: PropTypes.string,
	image: PropTypes.string,

	// Profile actions
	isSaving: PropTypes.bool,
	isSaved: PropTypes.bool,
	isDeleting: PropTypes.bool,
	onSaveProfile: PropTypes.func.isRequired,
	onDeleteProfile: PropTypes.func.isRequired,
	saveEmailError: ErrorPropTypeShape,

	// Password actions
	onSavePassword: PropTypes.func,
	isSavingPassword: PropTypes.bool,
	passwordIsSaved: PropTypes.bool,
	isSendingPasswordReset: PropTypes.bool,
	hasSendPasswordReset: PropTypes.bool,
	passWord: PropTypes.string,
	resetSaveMessage: PropTypes.func,
	passwordResetError: ErrorPropTypeShape,
};

ProfilePage.defaultProps = {
	email: "",
	userFirstName: "",
	userLastName: "",
	isSaving: false,
	isSaved: false,
	isSavingPassword: false,
	passwordIsSaved: false,
	isSendingPasswordReset: false,
	hasSendPasswordReset: false,
	saveEmailError: null,
	passwordResetError: null,
};

export default injectIntl( ProfilePage );
